"use client";

import Image from "next/image";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { Loader2, LogIn, LogOut, Menu, MoonStar, Radio, SunMedium, UserRound } from "lucide-react";
import { useMessages } from "next-intl";
import { createContext, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";

import { hasLoggedInFlagCookie } from "@/lib/auth/client-flag";
import type { SiteCopy, SiteLanguage } from "@/lib/site-data";
import { languageFromPathname, stripLanguagePrefix, withLanguagePrefix } from "@/lib/site-locale";
import { formatEventLocationLabel } from "@/lib/site-preferences";
import { EventLocationPicker } from "@/components/site/event-location-picker";
import { usePreferences } from "@/components/site/preferences-provider";
import { ThatRadioThingClient } from "@/components/site/thatradiothing-client";
import { useThatRadioThingSocket } from "@/lib/thatradiothing/socket-context";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import dynamic from "next/dynamic";

/**
 * Side-channel for letting deep page components ask the shell to
 * open its primary Sheet (settings + filters + radio dock). Used by
 * the events page's empty-filter CTA so the user can jump straight
 * into the location picker without hunting for the hamburger.
 *
 * The sheet itself is owned by ``SiteShell``; the context just hands
 * out the ``setMenuOpen(true)`` setter under a friendlier name.
 */
type SiteShellContextValue = {
  openSettings: () => void;
};

const SiteShellContext = createContext<SiteShellContextValue | null>(null);

export function useSiteShell(): SiteShellContextValue {
  const value = useContext(SiteShellContext);
  if (!value) {
    throw new Error("useSiteShell must be used inside SiteShell");
  }
  return value;
}

/**
 * This is client side only component because it contains the cookie consent bar and currently we don't wanna bother with cookie SSR logic.
 * Plus consideration of this with cloudflare edge caching is necessary if cookie will be used to render via SSR in the future.
 */
const CookieConsentBar = dynamic(
  () => import("@/components/site/cookie-consent-bar").then((mod) => mod.CookieConsentBar),
  { ssr: false }
);

// Nav items — keep the URL schema 1:1 with the routes under
// `src/app/[lang]/`. The `key` drives which label in the translation
// bundle we pull out.
// Primary nav order: Home → Discover → Events → News → Stories.
// Discover comes right after Home because it's the main "start
// browsing" surface; the wire-style pages (Events / News / Stories)
// follow in order of freshness. Genres + Artists indexes live under
// /discover (the deep-link routes `/genres/[slug]` and
// `/artists/[name]` are unchanged).
const navItems: Array<{ href: `/${string}`; key: "home" | "discover" | "events" | "news" | "clusters" }> = [
  { href: "/", key: "home" },
  { href: "/discover", key: "discover" },
  { href: "/events", key: "events" },
  { href: "/news", key: "news" },
  { href: "/clusters", key: "clusters" },
];

type AuthMeResponse = {
  authenticated: boolean;
  user?: {
    displayName?: string | null;
    imageUrl?: string | null;
  };
};

type HeaderAuthUser = {
  displayName: string;
  imageUrl: string | null;
};

function languageLabel(lang: SiteLanguage): string {
  if (lang === "tr") return "TR";
  if (lang === "uk") return "UA";
  if (lang === "de") return "DE";
  return "EN";
}

// LocalStorage key for the user's "hide floating radio" preference;
// persists across navigations and reloads.
const STORAGE_RADIO_DISMISSED = "duudey.radioClientDismissed";
const subscribeNoop = () => () => {};
const MICRO_BAR_HIDE_SCROLL_Y = 96;
const MICRO_BAR_SHOW_AT_TOP_Y = 8;
const MICRO_BAR_SCROLL_INTENT_PX = 8;
const MICRO_BAR_TOGGLE_SETTLE_MS = 360;

function readPersistedRadioDismissed(): boolean {
  // Read after mount, never during the lazy initializer of `useState`:
  // the first client render must produce the same HTML as SSR
  // (where ``window`` is undefined and we cannot reach ``localStorage``).
  // If we returned the localStorage value here, a returning visitor
  // would render with the radio hidden client-side while the SSR HTML
  // still showed it — a guaranteed React #418 hydration mismatch.
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(STORAGE_RADIO_DISMISSED) === "1";
  } catch {
    return false;
  }
}


/**
 * SiteShell — the outer chrome for every page.
 *
 * Layout notes (post-redesign):
 * - Header is a quiet, editorial band: logo + wordmark on the left,
 *   primary nav row in the middle (desktop only), and an actions cluster
 *   on the right (language / theme / account + hamburger).
 * - On mobile (`<md`) the primary nav collapses into the hamburger
 *   `Sheet`; the settings + preferences UI also lives inside that sheet
 *   so the main viewport is never cluttered with chrome.
 * - The floating radio dock continues to follow the user's prior
 *   decision: big inline on the home page, floating elsewhere, dismissible,
 *   state persisted to localStorage. This is NOT redesigned.
 */
export function SiteShell({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const router = useRouter();
  const {
    language,
    setLanguage,
    sourceOptions,
    selectedSourceGroups,
    setSelectedSourceGroups,
    sourceFilterMode,
    setSourceFilterMode,
    selectedSourceIds,
    setSelectedSourceIds,
    eventLocation,
    eventRadiusKm,
    theme,
    toggleTheme,
  } = usePreferences();

  const [menuOpen, setMenuOpen] = useState(false);
  const [radioClientOpen, setRadioClientOpen] = useState(false);
  // Always start with the SSR-deterministic default. The persisted
  // value is read after hydration in the effect below.
  const [radioClientDismissed, setRadioClientDismissed] = useState<boolean>(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [authMenuOpen, setAuthMenuOpen] = useState(false);
  const [authUser, setAuthUser] = useState<HeaderAuthUser | null>(null);
  // True while the user is currently scrolling *down* past the
  // initial offset. Drives the collapsing top micro-bar — we hide it
  // on the way down so the header takes less vertical space, and we
  // bring it back the moment the user scrolls up so they don't have
  // to reach the top of the page to see brand/date again. A simple
  // direction flip with a small threshold; no library needed.
  const [microBarHidden, setMicroBarHidden] = useState(false);
  const microBarHiddenRef = useRef(false);
  const authMenuRef = useRef<HTMLDivElement | null>(null);

  // The URL is authoritative. `languageFromPathname` answers `"en"` for
  // `/` — the bare root *is* the English root, and the server has
  // already redirected anyone with a non-English preference away from
  // it — so the shell never renders Turkish chrome around English
  // content just because the cookie says Turkish.
  const urlLanguage = languageFromPathname(pathname);
  const currentLanguage: SiteLanguage = urlLanguage ?? language;
  const normalizedPathname = stripLanguagePrefix(pathname);
  const copy = useMessages() as SiteCopy;
  const contentMaxWidthClass = normalizedPathname === "/" ? "max-w-[100rem]" : "max-w-7xl";
  const isHomePage = normalizedPathname === "/";
  const isHydrated = useSyncExternalStore(
    subscribeNoop,
    () => true,
    () => false,
  );

  // Keep the URL-derived language and the saved preference in sync.
  //
  // The URL is authoritative for *this* request — visiting `/de/news`
  // with a Turkish preference renders German and is never redirected.
  // Writing the preference back is what makes the next visit to `/` land
  // in the language the reader was last actually reading, which is the
  // behaviour the site has always had (next-intl used to do this from
  // the server with a `Set-Cookie` header; the write now happens in the
  // browser so page responses stay cacheable).
  // It depends on the *URL* and nothing else. Deliberately not on
  // `language`: the switcher sets `language` first and then navigates,
  // so for the duration of that navigation the two disagree. An effect
  // that watched `language` would fire during exactly that window, see
  // the old URL, and write the old language straight back over the
  // reader's choice — which, when the destination is `/`, is enough to
  // make the proxy redirect them back where they came from.
  //
  // With `urlLanguage` as the only trigger the effect stays quiet until
  // the navigation actually commits, and then records the language the
  // reader ended up in.
  useEffect(() => {
    if (!urlLanguage) return;
    setLanguage(urlLanguage);
  }, [urlLanguage, setLanguage]);

  useEffect(() => {
    // Pull the persisted radio-dismissed flag now that we are on the
    // client. This may flip the dock open or shut depending on what
    // the user did on a previous visit; React reconciles the new state
    // after hydration completes, which is exactly when this fires.
    // eslint-disable-next-line react-hooks/set-state-in-effect -- The localStorage-backed dismissal preference is intentionally applied only after the SSR-matching first render.
    setRadioClientDismissed(readPersistedRadioDismissed());
  }, []);

  /**
   * Direction-aware scroll watcher for the collapsing top micro-bar.
   *
   * Behaviour we want:
   *   - At the top of the page the bar is fully visible.
   *   - As the user scrolls down past a small offset (so a tiny
   *     bounce or focus-induced 1px scroll never collapses it), the
   *     bar slides up out of view.
   *   - The instant the user scrolls *up* by more than a few pixels,
   *     the bar comes back — even if they're still deep in the page.
   *
   * Implementation: we sample ``scrollY`` inside ``requestAnimationFrame``
   * to coalesce bursts of scroll events, compare against the last
   * sample, and apply a small threshold so micro-jitter doesn't toggle
   * state every frame. After toggling we briefly ignore scroll deltas:
   * the bar's own ``max-height`` transition can shift the document by
   * a few pixels on some touch devices, and that synthetic movement
   * should not be interpreted as the user reversing direction.
   */
  useEffect(() => {
    let lastScrollY = typeof window !== "undefined" ? window.scrollY : 0;
    let pendingFrame = 0;
    let ignoreScrollUntil = 0;

    function setMicroBarVisibility(hidden: boolean) {
      microBarHiddenRef.current = hidden;
      setMicroBarHidden(hidden);
      ignoreScrollUntil = window.performance.now() + MICRO_BAR_TOGGLE_SETTLE_MS;
    }

    function evaluate() {
      pendingFrame = 0;
      const currentY = window.scrollY;
      const delta = currentY - lastScrollY;

      if (window.performance.now() < ignoreScrollUntil) {
        lastScrollY = currentY;
        return;
      }

      // Past the visual fold *and* still moving down → hide.
      if (!microBarHiddenRef.current && delta > MICRO_BAR_SCROLL_INTENT_PX && currentY > MICRO_BAR_HIDE_SCROLL_Y) {
        setMicroBarVisibility(true);
      } else if (microBarHiddenRef.current && (delta < -MICRO_BAR_SCROLL_INTENT_PX || currentY <= MICRO_BAR_SHOW_AT_TOP_Y)) {
        // Any meaningful upward motion, or sitting at the top of the
        // page, brings the bar back.
        setMicroBarVisibility(false);
      }
      lastScrollY = currentY;
    }

    function onScroll() {
      if (pendingFrame !== 0) return;
      pendingFrame = window.requestAnimationFrame(evaluate);
    }

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (pendingFrame !== 0) {
        window.cancelAnimationFrame(pendingFrame);
      }
    };
  }, []);

  // Persist the dismissed flag so hiding the floating player stays
  // sticky across page navigations *and* full reloads.
  useEffect(() => {
    try {
      if (radioClientDismissed) {
        window.localStorage.setItem(STORAGE_RADIO_DISMISSED, "1");
      } else {
        window.localStorage.removeItem(STORAGE_RADIO_DISMISSED);
      }
    } catch {
      // localStorage blocked (private mode, storage quota) — best-effort.
    }
  }, [radioClientDismissed]);

  // Collapse menus on route changes so the new page isn't obscured by
  // a stale popover/sheet.
  useEffect(() => {
    /* eslint-disable react-hooks/set-state-in-effect --
       Route changes intentionally collapse transient UI chrome. This
       is a post-navigation cleanup step, not derived render state. */
    setAuthMenuOpen(false);
    setMenuOpen(false);
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [pathname]);

  // Click-outside for the auth dropdown.
  useEffect(() => {
    if (!authMenuOpen) {
      return;
    }

    function handlePointerDown(event: MouseEvent) {
      const target = event.target;
      if (target instanceof Node && authMenuRef.current && !authMenuRef.current.contains(target)) {
        setAuthMenuOpen(false);
      }
    }

    document.addEventListener("mousedown", handlePointerDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
    };
  }, [authMenuOpen]);

  // Subscribe to the layout-scoped Socket.IO connection (owned by
  // ``ThatRadioThingSocketProvider`` in ``[lang]/layout.tsx``) instead of
  // opening our own. This used to be a per-component ``io(...)`` call
  // whose effect re-ran on every navigation between home and non-home
  // pages, tearing down and rebuilding the websocket each time. Routing
  // the status through context lets one connection live for the entire
  // language-segment lifetime and serve every consumer (this header
  // indicator + the inline/floating radio card).
  //
  // The provider gates the connection on ``hasLoggedInFlagCookie()``, so
  // for guests ``status`` is just permanently ``null`` — no socket
  // opened, no traffic.
  const { status: radioStatus } = useThatRadioThingSocket();

  const radioPayload = radioStatus as { now_playing?: unknown; enabled?: boolean } | null;
  // Header animation can be derived directly from the latest socket
  // payload. Only the dock-open side effect needs to stay in an effect.
  const floatingPlaybackActive = !isHomePage && Boolean(radioPayload?.enabled) && Boolean(radioPayload?.now_playing);

  // Drive the auto-surface dock from the shared status payload. The
  // home page already shows the inline radio card, so auto-opening the
  // floating dock there would be redundant.
  useEffect(() => {
    if (floatingPlaybackActive && !radioClientOpen && !radioClientDismissed) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- This effect mirrors an external socket status change into the dock's open/closed UI state.
      setRadioClientOpen(true);
    }
  }, [floatingPlaybackActive, radioClientOpen, radioClientDismissed]);

  function toggleFloatingRadioClient() {
    setRadioClientOpen((current) => {
      const next = !current;
      setRadioClientDismissed(!next);
      return next;
    });
  }

  const selectedSourceCount = useMemo(() => {
    if (selectedSourceIds.length > 0) return selectedSourceIds.length;
    return sourceOptions.filter((item) => item.defaultSelected).length;
  }, [selectedSourceIds, sourceOptions]);

  function toggleSource(sourceId: string) {
    const exists = selectedSourceIds.includes(sourceId);
    if (exists) {
      const next = selectedSourceIds.filter((id) => id !== sourceId);
      setSelectedSourceIds(next.length > 0 ? next : [sourceId]);
      return;
    }
    setSelectedSourceIds([...selectedSourceIds, sourceId]);
  }

  function toggleSourceGroup(group: "tr" | "en") {
    if (selectedSourceGroups.includes(group)) {
      const next = selectedSourceGroups.filter((item) => item !== group);
      setSelectedSourceGroups(next.length > 0 ? next : [group]);
      return;
    }
    setSelectedSourceGroups([...selectedSourceGroups, group]);
  }

  function switchLanguage(nextLanguage: SiteLanguage) {
    setLanguage(nextLanguage);
    router.push(withLanguagePrefix(pathname, nextLanguage));
  }

  async function loadAuthState() {
    // The `duudey_logged_in` flag cookie (set by the server in lockstep
    // with the real JWT) tells us — without a network round-trip —
    // whether this browser has *any* session at all. Guests don't have
    // it, so we skip the `/api/auth/me` probe entirely and render the
    // login button immediately. Logged-in users do have it, so we
    // proceed with the probe to fetch the user details for the avatar.
    //
    // The flag is a render hint, never an authorization signal. The
    // server still verifies the HttpOnly JWT on every request that
    // matters; if the flag is forged or stale, the worst-case outcome
    // is one wasted probe whose 200/`{authenticated:false}` corrects
    // the UI a few hundred ms later.
    if (typeof document !== "undefined" && !hasLoggedInFlagCookie()) {
      setAuthUser(null);
      setAuthLoading(false);
      return;
    }

    try {
      setAuthLoading(true);
      const response = await fetch("/api/auth/me", { cache: "no-store" });
      const payload = (await response.json()) as AuthMeResponse;

      if (!response.ok || !payload.authenticated) {
        setAuthUser(null);
        return;
      }

      setAuthUser({
        displayName: payload.user?.displayName ?? "Spotify user",
        imageUrl: payload.user?.imageUrl ?? null,
      });
    } catch {
      setAuthUser(null);
    } finally {
      setAuthLoading(false);
    }
  }

  function startSpotifyLogin() {
    if (typeof window === "undefined") return;
    const returnTo = `${window.location.pathname}${window.location.search}`;
    const origin = window.location.origin;
    window.location.href = `/api/auth/spotify/login?returnTo=${encodeURIComponent(returnTo)}&origin=${encodeURIComponent(origin)}`;
  }

  async function logout() {
    try {
      await fetch("/api/auth/logout", { method: "POST" });
    } finally {
      setAuthUser(null);
      setAuthMenuOpen(false);
      window.location.reload();
    }
  }

  useEffect(() => {
    // Auth state is fetched once per mount, not per navigation. SiteShell
    // is mounted by ``[lang]/layout.tsx`` and persists across client-side
    // route changes within a language, so re-probing on every ``pathname``
    // change is wasted work — the user's display name and avatar don't
    // depend on the current route. The two events that *do* change auth
    // state both trigger a full page reload, which remounts this effect:
    //   • ``logout()`` calls ``window.location.reload()`` after the POST.
    //   • The Spotify OAuth callback redirects (full navigation) back to
    //     the originating page once the cookie is set.
    // Switching languages also remounts the layout (different ``[lang]``
    // segment), so the effect re-runs there too.
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Mount-time auth bootstrap intentionally populates header state after the first paint.
    void loadAuthState();
  }, []);

  // ---- Render ---------------------------------------------------------

  // Memoised context value so children that consume only
  // ``openSettings`` don't re-render every time SiteShell does.
  const shellContextValue = useMemo<SiteShellContextValue>(
    () => ({ openSettings: () => setMenuOpen(true) }),
    [],
  );

  return (
   <SiteShellContext.Provider value={shellContextValue}>
    <div className="relative min-h-screen bg-background text-foreground">
      <div className="aurora" />
      <div className="noise" />

      <header className="sticky top-0 z-40 border-b border-border bg-background/85 backdrop-blur-xl">
        {/* Top micro-bar — tiny "d" logo + brand badge on the left,
            date on the right. Small, uppercase, kerned. Moving the logo
            here frees the main header of the logo+wordmark combo that
            read as redundant.

            Collapsing wrapper: when the user scrolls down past the
            fold we shrink ``max-height`` to ``0`` (with
            ``overflow-hidden`` clipping the inner content + its
            bottom border). On scroll-up it re-expands. The transition
            target is ``max-height``, not ``height`` — there's no way
            to animate ``height: auto`` directly, but a max-height
            ceiling is enough as long as the real content fits inside
            it (the strip is ~33 px; ``max-h-12`` = 48 px gives a safe
            ceiling without a visible jump). */}
        <div
          aria-hidden={microBarHidden}
          className={cn(
            "overflow-hidden transition-[max-height] duration-300 ease-out motion-reduce:transition-none",
            microBarHidden ? "max-h-0" : "max-h-12",
          )}
        >
         <div className="border-b border-border bg-card/60">
          <div
            className={cn(
              "mx-auto flex w-full items-center justify-between gap-4 px-4 py-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground sm:px-6 lg:px-8",
              contentMaxWidthClass,
            )}
          >
            <div className="flex items-center gap-2">
              {/* Logo swap per theme — the green glyph disappears at
                  this size in dark mode, so we serve the rose variant
                  there. Both variants render; Tailwind's `dark:`
                  class variant toggles visibility so the transition
                  is instant and hydration-safe (no useEffect flicker). */}
              <span className="relative inline-block size-4 shrink-0 overflow-hidden">
                <Image
                  src="/logo.png"
                  alt="duudey"
                  fill
                  sizes="16px"
                  className="object-contain dark:hidden"
                  priority
                />
                <Image
                  src="/logo-rose.png"
                  alt=""
                  aria-hidden="true"
                  fill
                  sizes="16px"
                  className="hidden object-contain dark:block"
                  priority
                />
              </span>
              <p>{copy.ui.brandBadge}</p>
            </div>
            <p className="font-mono">{new Date().toISOString().slice(0, 10)}</p>
          </div>
         </div>
        </div>

        {/* Main header row — 3-column grid so the nav is truly
            centered regardless of how wide the logo or actions get.
            `items-center` keeps everything vertically aligned. */}
        <div
          className={cn(
            "mx-auto grid w-full items-center gap-4 px-4 py-4 sm:px-6 lg:px-8",
            "grid-cols-[auto_1fr_auto] md:grid-cols-[1fr_auto_1fr]",
            contentMaxWidthClass,
          )}
        >
          {/* Wordmark-only home link. The small "d" logo now lives in
              the top micro-bar (see above), which keeps this row clean
              and avoids the logo+wordmark redundancy that felt off. */}
          <Link
            href={withLanguagePrefix("/", currentLanguage)}
            className="justify-self-start leading-tight"
          >
            <p className="text-2xl font-black tracking-tight text-foreground">duudey</p>
            {/* Tagline visible only on very wide screens where there's
                room for it; otherwise it squeezed the centered nav. */}
            <p className="hidden text-[11px] text-muted-foreground xl:block">{copy.siteTagline}</p>
          </Link>

          {/* Desktop primary nav — quiet text row, underline-on-active, centered in its grid cell. */}
          <nav className="hidden items-center justify-center gap-6 md:flex">
            {navItems.map((item) => {
              const active = normalizedPathname === item.href;
              return (
                <Link
                  key={item.href}
                  href={withLanguagePrefix(item.href, currentLanguage)}
                  className={cn(
                    "relative text-sm font-medium tracking-tight transition",
                    active
                      ? "text-foreground"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {copy.nav[item.key]}
                  {active ? (
                    <span
                      aria-hidden="true"
                      className="absolute inset-x-0 -bottom-1 h-px rounded-full bg-rose-500"
                    />
                  ) : null}
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-2 justify-self-end">
            {/* Radio toggle — only on non-home pages where the floating
                dock is relevant. The home page keeps its dedicated
                inline `ThatRadioThingClient` card, so this button isn't
                needed there. Gets the `.radio-activity-border` glow
                when the user is actively playing but has the dock
                hidden. */}
            {!isHomePage ? (
              <Button
                type="button"
                onClick={toggleFloatingRadioClient}
                variant="outline"
                size="icon"
                title={radioClientOpen ? copy.ui.hideRadioClient : copy.ui.showRadioClient}
                aria-label={radioClientOpen ? copy.ui.hideRadioClient : copy.ui.showRadioClient}
                className={cn(
                  "size-11 rounded-full border-border bg-muted text-foreground hover:bg-accent",
                  !radioClientOpen && floatingPlaybackActive ? "radio-activity-border" : null,
                )}
              >
                <Radio className="size-4" />
              </Button>
            ) : null}

            {/* Theme toggle — stays in the header at all breakpoints;
                too high-touch to hide behind the hamburger. */}
            <Button
              type="button"
              onClick={toggleTheme}
              variant="outline"
              size="icon"
              title={theme === "dark" ? copy.ui.dark : copy.ui.light}
              aria-label={theme === "dark" ? copy.ui.dark : copy.ui.light}
              className="size-11 rounded-full border-border bg-muted text-foreground hover:bg-accent"
            >
              {theme === "dark" ? <MoonStar className="size-4" /> : <SunMedium className="size-4" />}
            </Button>

            {/* Account button — always visible at every breakpoint.
                Account is a primary action (login, switch user, see
                display name), not a setting — it belongs in the
                header, not the settings Sheet. */}
            <div ref={authMenuRef} className="relative">
              <Button
                type="button"
                onClick={() => setAuthMenuOpen((current) => !current)}
                variant="outline"
                size="icon"
                title={authUser ? authUser.displayName : "Account"}
                aria-label={authUser ? authUser.displayName : "Account"}
                className="size-11 rounded-full border-border bg-muted text-foreground hover:bg-accent"
              >
                {authLoading ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : authUser?.imageUrl ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img src={authUser.imageUrl} alt={authUser.displayName} className="size-7 rounded-full object-cover" />
                ) : authUser ? (
                  <UserRound className="size-4" />
                ) : (
                  <LogIn className="size-4" />
                )}
              </Button>

              {authMenuOpen ? (
                <div className="absolute right-0 top-[calc(100%+0.45rem)] z-50 w-64 space-y-2 rounded-xl border border-border bg-popover p-2.5 shadow-2xl">
                  {authLoading ? (
                    <div className="flex items-center gap-2 rounded-lg border border-border bg-muted px-3 py-2 text-xs text-foreground">
                      <Loader2 className="size-3.5 animate-spin" />
                      Checking session...
                    </div>
                  ) : authUser ? (
                    <>
                      <div className="flex items-center gap-2 rounded-lg border border-border bg-muted px-3 py-2">
                        {authUser.imageUrl ? (
                          // eslint-disable-next-line @next/next/no-img-element
                          <img src={authUser.imageUrl} alt={authUser.displayName} className="size-8 rounded-full object-cover" />
                        ) : (
                          <div className="inline-flex size-8 items-center justify-center rounded-full border border-border bg-accent text-foreground">
                            <UserRound className="size-4" />
                          </div>
                        )}
                        <p className="truncate text-sm font-medium text-foreground">{authUser.displayName}</p>
                      </div>

                      <Button
                        type="button"
                        onClick={() => void logout()}
                        size="sm"
                        variant="outline"
                        className="w-full rounded-lg border-border bg-muted text-foreground hover:bg-accent"
                      >
                        <LogOut className="mr-2 size-4" />
                        Logout
                      </Button>
                    </>
                  ) : (
                    <Button
                      type="button"
                      onClick={startSpotifyLogin}
                      size="sm"
                      className="w-full rounded-lg bg-emerald-300 text-zinc-900 hover:bg-emerald-200"
                    >
                      <LogIn className="mr-2 size-4" />
                      Login with Spotify
                    </Button>
                  )}
                </div>
              ) : null}
            </div>

            {/* Hamburger — always visible. Opens the universal Sheet
                with language, settings, location picker, account on
                mobile, and (on mobile only) the primary nav. This is
                the single chrome entry point the plan commits to. */}
            <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
              <SheetTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  title={copy.ui.settings}
                  aria-label={copy.ui.settings}
                  className="size-11 rounded-full border-border bg-muted text-foreground hover:bg-accent"
                >
                  <Menu className="size-4" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full gap-0 overflow-y-auto pb-[env(safe-area-inset-bottom)] sm:max-w-md">
                <SheetHeader>
                  <SheetTitle>{copy.ui.settings}</SheetTitle>
                  <SheetDescription className="sr-only">Primary navigation and preferences.</SheetDescription>
                </SheetHeader>

                {/* Primary nav, mobile only — desktop has it in the
                    header bar already, so duplicating it here would add
                    churn without benefit. */}
                <nav className="grid gap-1 px-4 md:hidden">
                  {navItems.map((item) => {
                    const active = normalizedPathname === item.href;
                    return (
                      <Link
                        key={item.href}
                        href={withLanguagePrefix(item.href, currentLanguage)}
                        className={cn(
                          "rounded-xl px-3 py-2.5 text-base font-semibold transition",
                          active
                            ? "bg-rose-400/15 text-rose-700 dark:text-rose-200"
                            : "text-foreground hover:bg-muted",
                        )}
                      >
                        {copy.nav[item.key]}
                      </Link>
                    );
                  })}
                </nav>

                <div className="space-y-5 px-4 py-4">
                  {/* Language picker */}
                  <section>
                    <p className="mb-2 text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">Language</p>
                    <div className="flex flex-wrap gap-1.5">
                      {(["en", "tr", "de", "uk"] as SiteLanguage[]).map((lang) => {
                        const active = currentLanguage === lang;
                        return (
                          <Button
                            key={lang}
                            type="button"
                            size="sm"
                            variant={active ? "secondary" : "outline"}
                            onClick={() => switchLanguage(lang)}
                            className={cn(
                              "rounded-full border px-3",
                              active
                                ? "border-rose-300/50 bg-rose-400/20 text-rose-700 hover:bg-rose-400/20 dark:text-rose-100"
                                : "border-border bg-muted text-foreground hover:bg-accent",
                            )}
                          >
                            {languageLabel(lang)}
                          </Button>
                        );
                      })}
                    </div>
                  </section>

                  {/* News sources */}
                  <section>
                    <p className="mb-2 text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">{copy.ui.newsSources}</p>
                    <div className="flex flex-wrap gap-1.5">
                      <Button
                        type="button"
                        size="sm"
                        variant={selectedSourceGroups.includes("tr") ? "secondary" : "outline"}
                        onClick={() => {
                          setSourceFilterMode("default");
                          toggleSourceGroup("tr");
                        }}
                        className={cn(
                          "rounded-full border px-3",
                          selectedSourceGroups.includes("tr")
                            ? "border-rose-300/50 bg-rose-400/20 text-rose-700 dark:text-rose-100"
                            : "border-border bg-muted text-foreground hover:bg-accent",
                        )}
                      >
                        {copy.ui.turkeyTR}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant={selectedSourceGroups.includes("en") ? "secondary" : "outline"}
                        onClick={() => {
                          setSourceFilterMode("default");
                          toggleSourceGroup("en");
                        }}
                        className={cn(
                          "rounded-full border px-3",
                          selectedSourceGroups.includes("en")
                            ? "border-rose-300/50 bg-rose-400/20 text-rose-700 dark:text-rose-100"
                            : "border-border bg-muted text-foreground hover:bg-accent",
                        )}
                      >
                        {copy.ui.globalEN}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant={sourceFilterMode === "advanced" ? "secondary" : "outline"}
                        onClick={() => setSourceFilterMode(sourceFilterMode === "advanced" ? "default" : "advanced")}
                        className={cn(
                          "rounded-full border px-3",
                          sourceFilterMode === "advanced"
                            ? "border-rose-300/50 bg-rose-400/20 text-rose-700 dark:text-rose-100"
                            : "border-border bg-muted text-foreground hover:bg-accent",
                        )}
                      >
                        {copy.ui.advanced}
                      </Button>
                    </div>

                    {sourceFilterMode === "advanced" ? (
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {sourceOptions.map((source) => {
                          const active = selectedSourceIds.includes(source.id);
                          return (
                            <Button
                              key={source.id}
                              type="button"
                              size="sm"
                              variant={active ? "secondary" : "outline"}
                              onClick={() => toggleSource(source.id)}
                              className={cn(
                                "rounded-full border px-3 text-xs",
                                active
                                  ? "border-rose-300/50 bg-rose-400/20 text-rose-700 dark:text-rose-100"
                                  : "border-border bg-muted text-foreground hover:bg-accent",
                              )}
                            >
                              {source.name} {(source.language ?? "--").toUpperCase()}
                            </Button>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="mt-2 text-xs text-muted-foreground">{copy.ui.defaultPicks}</p>
                    )}
                  </section>

                  {/* Event location picker — the component is a rich
                      surface on its own; we just give it room here. */}
                  <section>
                    <p className="mb-2 text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">{copy.ui.eventLocation}</p>
                    <EventLocationPicker />
                  </section>

                  {/* Current-selection pulse — condensed, informational. */}
                  <section className="rounded-xl border border-border bg-muted p-3 text-xs text-foreground">
                    <p className="flex items-center gap-2">
                      <span className="rounded-full border border-rose-300/40 bg-rose-400/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.14em] text-rose-700 dark:text-rose-200">
                        Pulse
                      </span>
                      <span>
                        {copy.ui.pulseMeta
                          .replace("{sources}", isHydrated ? String(selectedSourceCount) : copy.ui.pulseMetaUnknown)
                          .replace(
                            "{events}",
                            isHydrated
                              ? eventLocation
                                ? `${formatEventLocationLabel(eventLocation) || copy.ui.pulseMetaUnknown} · ${eventRadiusKm}km`
                                : copy.ui.pulseMetaUnknown
                              : copy.ui.pulseMetaUnknown,
                          )}
                      </span>
                    </p>
                  </section>

                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
        <CookieConsentBar />
      </header>

      <main className={cn("relative z-10 mx-auto w-full px-4 py-10 sm:px-6 lg:px-8", contentMaxWidthClass)}>
        {children}
      </main>

      {/* Floating radio dock — PRESERVED unchanged per prior design:
          big inline card on Home (handled by HomeView), floating and
          user-dismissible here. Do not repurpose. */}
      {!isHomePage && radioClientOpen ? (
        <div className="fixed bottom-4 right-4 z-50 max-sm:left-4 max-sm:right-4">
          <ThatRadioThingClient
            mode="floating"
            title={copy.ui.radioClientTitle}
            subtitle={copy.ui.radioClientSubtitle}
            openLabel={copy.ui.openRadioClient}
            hideLabel={copy.ui.hideRadioClient}
            onHide={() => {
              setRadioClientOpen(false);
              setRadioClientDismissed(true);
            }}
          />
        </div>
      ) : null}
    </div>
   </SiteShellContext.Provider>
  );
}
